/**
 * Tool Compatibility Evaluator (v2.2 Canon)
 * 
 * Per Terminology v2.2:
 * "For S6 sessions, compute compatibility_status (compatible, compatible_with_warnings,
 * incompatible) based on inputs like criteria count, scale types, pool status, 
 * values sufficiency, participation."
 * 
 * Tool selection is part of Session Configuration. Tool is locked after session start.
 * 
 * This evaluator:
 * 1. Checks if a tool is compatible with the current session context
 * 2. Generates warnings for potential issues
 * 3. Blocks session start if tool is incompatible
 */

import type { Attribute } from '../types/prioritization/attributes';
import type { Item } from '../types/prioritization/lifecycle';
import { REGISTRY_SESSION_TOOL_IDS, REGISTRY_TOOL_SPECS_BY_ID } from '../generated/registryToolSpecs';
import { normalizeSessionToolId, SESSION_TOOL_BINDINGS } from './sessionToolCatalog';

// ============================================================================
// Types
// ============================================================================

/**
 * Tool compatibility status
 */
export type ToolCompatibilityStatus = 
  | 'compatible'
  | 'compatible_with_warnings'
  | 'incompatible';

/**
 * A compatibility issue (warning or blocker)
 */
export interface CompatibilityIssue {
  /** Issue code for programmatic handling */
  code: string;
  
  /** Severity: 'error' blocks session start, 'warning' allows but notifies */
  severity: 'error' | 'warning';
  
  /** Human-readable title */
  title: string;
  
  /** Detailed description */
  description: string;
  
  /** Suggested remediation */
  remediation?: string;
}

/**
 * Result of tool compatibility evaluation
 */
export interface ToolCompatibilityResult {
  /** Tool ID being evaluated */
  toolId: string;
  
  /** Overall status */
  status: ToolCompatibilityStatus;
  
  /** All issues found */
  issues: CompatibilityIssue[];
  
  /** Blocking issues (errors) */
  blockers: CompatibilityIssue[];
  
  /** Non-blocking issues (warnings) */
  warnings: CompatibilityIssue[];
  
  /** Whether the tool can be used */
  canUse: boolean;
  
  /** Summary message */
  summary: string;
}

/**
 * Context for tool compatibility evaluation
 */
export interface ToolCompatibilityContext {
  /** The tool being evaluated */
  toolId: string;
  
  /**
   * REQUIRED (v2.3): Track ID for scoped evaluation.
   * Per Track-Authoritative Cycle Spec: Tool compatibility is evaluated per-track.
   * When provided, items/criteria/values are filtered by this track.
   */
  trackId?: string;
  
  /** Tool metadata (if available) */
  toolMeta?: {
    name: string;
    family: string;
    minItems?: number;
    maxItems?: number;
    requiresCriteria?: boolean;
    supportedScaleTypes?: string[];
    supportsWeights?: boolean;
  };
  
  /** Items in the session pool */
  items: Item[];
  
  /** Criteria selected for the session */
  criteria: Attribute[];
  
  /** Whether pool is locked */
  poolLocked: boolean;
  
  /** Whether criteria are locked */
  criteriaLocked: boolean;
  
  /** Whether values are sufficient */
  valuesSufficient: boolean;
  
  /** Value completion percentage per criterion */
  valueCompletionByAttribute: Record<string, number>;
  
  /** Minimum required value completion */
  minimumValueCompletionPercent: number;
  
  /** Whether participation is locked */
  participationLocked: boolean;
  
  /** Number of participants */
  participantCount: number;
}

// ============================================================================
// Tool Metadata (for common tools)
// ============================================================================

type ToolFamily = 'ranking' | 'voting' | 'scoring' | 'matrix' | 'pairwise';

interface ToolDefinition {
  id: string;
  name: string;
  family: ToolFamily;
  prerequisites: string[];
  minItems: number;
  maxItems?: number;
  requiresCriteria: boolean;
  /** Tool owns its evaluation dimensions (Impact/Effort, RICE, etc.) — no workspace criteria rows required. */
  toolDefinesCriteria?: boolean;
  supportedScaleTypes: string[];
  supportsWeights: boolean;
  minParticipants?: number;
  /**
   * Specific attribute keys this tool needs as session criteria (e.g. ["impact", "effort"]).
   * The evaluator checks that each named field exists in context.criteria by key.
   */
  requiredItemFields?: string[];
}

const LEGACY_TOOL_DEFINITIONS: Record<string, ToolDefinition> = {
  'eisenhower-matrix': {
    id: 'eisenhower-matrix',
    name: 'Eisenhower Matrix',
    family: 'matrix',
    prerequisites: ['POOL_LOCKED', 'CRITERIA_LOCKED'],
    minItems: 1,
    requiresCriteria: true,
    supportedScaleTypes: ['binary', 'ordinal'],
    supportsWeights: false,
  },
  kano: {
    id: 'kano',
    name: 'Kano Model',
    family: 'scoring',
    prerequisites: ['POOL_LOCKED', 'CRITERIA_LOCKED', 'VALUES_SUFFICIENT'],
    minItems: 1,
    requiresCriteria: true,
    supportedScaleTypes: ['ordinal', 'likert'],
    supportsWeights: false,
    minParticipants: 3,
  },
};

function uniqueStrings(values: Array<string | undefined>): string[] {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

function getRegistryToolAliases(toolId: string): string[] {
  const binding = SESSION_TOOL_BINDINGS[toolId];
  return uniqueStrings([toolId, binding?.uiId, ...(binding?.aliases ?? [])]);
}

function buildKnownTools(): Record<string, ToolDefinition> {
  const knownTools = new Map<string, ToolDefinition>();

  for (const toolId of REGISTRY_SESSION_TOOL_IDS) {
    const spec = REGISTRY_TOOL_SPECS_BY_ID[toolId];

    if (!spec) {
      throw new Error(`Missing registry spec for compatibility evaluator tool: ${toolId}`);
    }

    if (!spec.compatibility) {
      throw new Error(`Missing registry compatibility metadata for tool: ${toolId}`);
    }

    const definition: ToolDefinition = {
      id: toolId,
      name: spec.displayName,
      prerequisites: spec.prerequisites,
      requiredItemFields: spec.requiredItemFields?.length ? spec.requiredItemFields : undefined,
      ...spec.compatibility,
    };

    for (const alias of getRegistryToolAliases(toolId)) {
      knownTools.set(alias, definition);
    }
  }

  for (const [toolId, definition] of Object.entries(LEGACY_TOOL_DEFINITIONS)) {
    knownTools.set(toolId, definition);
  }

  return Object.fromEntries(knownTools.entries());
}

const CANONICAL_KNOWN_TOOL_IDS = [
  ...REGISTRY_SESSION_TOOL_IDS,
  ...Object.keys(LEGACY_TOOL_DEFINITIONS),
];

export const KNOWN_TOOLS: Record<string, ToolDefinition> = buildKnownTools();

function resolveKnownToolDefinition(
  toolId: string,
  toolMeta?: ToolCompatibilityContext['toolMeta']
): ToolDefinition | undefined {
  const baseDefinition = KNOWN_TOOLS[toolId];
  if (!baseDefinition) {
    return undefined;
  }

  return toolMeta
    ? {
        ...baseDefinition,
        ...toolMeta,
      }
    : baseDefinition;
}

function hasPrerequisite(toolDef: ToolDefinition, prerequisite: string): boolean {
  return toolDef.prerequisites.includes(prerequisite);
}

// ============================================================================
// Evaluation Functions
// ============================================================================

/**
 * Evaluate tool compatibility with current session context
 */
export function evaluateToolCompatibility(
  context: ToolCompatibilityContext
): ToolCompatibilityResult {
  const issues: CompatibilityIssue[] = [];
  const canonicalToolId = normalizeSessionToolId(context.toolId) ?? context.toolId;
  
  // Get tool metadata
  const toolDef = resolveKnownToolDefinition(canonicalToolId, context.toolMeta);
  
  // If unknown tool, return compatible with warning
  if (!toolDef) {
    return {
      toolId: canonicalToolId,
      status: 'compatible_with_warnings',
      issues: [{
        code: 'UNKNOWN_TOOL',
        severity: 'warning',
        title: 'Unknown Tool',
        description: `Tool '${canonicalToolId}' is not in the known tools registry. Compatibility cannot be fully verified.`,
      }],
      blockers: [],
      warnings: [{
        code: 'UNKNOWN_TOOL',
        severity: 'warning',
        title: 'Unknown Tool',
        description: `Tool '${canonicalToolId}' is not in the known tools registry. Compatibility cannot be fully verified.`,
      }],
      canUse: true,
      summary: 'Tool compatibility could not be fully verified.',
    };
  }
  
  // Check item count
  if (context.items.length < toolDef.minItems) {
    issues.push({
      code: 'INSUFFICIENT_ITEMS',
      severity: 'error',
      title: 'Not Enough Items',
      description: `${toolDef.name} requires at least ${toolDef.minItems} items. Current pool has ${context.items.length}.`,
      remediation: `Add at least ${toolDef.minItems - context.items.length} more items to the pool.`,
    });
  }
  
  if (toolDef.maxItems && context.items.length > toolDef.maxItems) {
    issues.push({
      code: 'TOO_MANY_ITEMS',
      severity: 'warning',
      title: 'Large Item Pool',
      description: `${toolDef.name} works best with ${toolDef.maxItems} or fewer items. Current pool has ${context.items.length}.`,
      remediation: `Consider reducing the pool size or using a different tool.`,
    });
  }
  
  // Check criteria requirements (skip when the tool defines its own dimensions)
  if (
    !toolDef.toolDefinesCriteria
    && (toolDef.requiresCriteria || hasPrerequisite(toolDef, 'CRITERIA_LOCKED'))
    && context.criteria.length === 0
  ) {
    issues.push({
      code: 'CRITERIA_REQUIRED',
      severity: 'error',
      title: 'Criteria Required',
      description: `${toolDef.name} requires at least one criterion to be selected.`,
      remediation: 'Select criteria before using this tool.',
    });
  }
  
  // Check scale type compatibility
  if ((toolDef.requiresCriteria || hasPrerequisite(toolDef, 'CRITERIA_LOCKED')) && toolDef.supportedScaleTypes.length > 0) {
    const incompatibleCriteria = context.criteria.filter(c => {
      const scaleType = c.scaleType ?? 'unknown';
      return !toolDef.supportedScaleTypes.includes(scaleType) && scaleType !== 'unknown';
    });
    
    if (incompatibleCriteria.length > 0) {
      issues.push({
        code: 'INCOMPATIBLE_SCALE_TYPES',
        severity: 'warning',
        title: 'Scale Type Mismatch',
        description: `${incompatibleCriteria.length} criteria have scale types not optimized for ${toolDef.name}.`,
        remediation: `Review scale type configuration for: ${incompatibleCriteria.map(c => c.name).join(', ')}`,
      });
    }
  }
  
  // Check participant requirements
  if (toolDef.minParticipants && context.participantCount < toolDef.minParticipants) {
    issues.push({
      code: 'INSUFFICIENT_PARTICIPANTS',
      severity: 'error',
      title: 'Not Enough Participants',
      description: `${toolDef.name} requires at least ${toolDef.minParticipants} participants. Currently ${context.participantCount}.`,
      remediation: `Add ${toolDef.minParticipants - context.participantCount} more participants.`,
    });
  }
  
  // Check prerequisite locks
  if (hasPrerequisite(toolDef, 'POOL_LOCKED') && !context.poolLocked) {
    issues.push({
      code: 'POOL_NOT_LOCKED',
      severity: 'error',
      title: 'Pool Not Locked',
      description: 'The item pool must be locked before running a prioritization tool.',
      remediation: 'Lock the pool via Pool Selection.',
    });
  }
  
  if (hasPrerequisite(toolDef, 'CRITERIA_LOCKED') && !context.criteriaLocked) {
    issues.push({
      code: 'CRITERIA_NOT_LOCKED',
      severity: 'error',
      title: 'Criteria Not Locked',
      description: 'Criteria must be locked before running this tool.',
      remediation: 'Lock criteria via Criteria Selection.',
    });
  }
  
  // Check values sufficiency
  if (hasPrerequisite(toolDef, 'VALUES_SUFFICIENT') && !context.valuesSufficient) {
    const lowCompletionCriteria = Object.entries(context.valueCompletionByAttribute)
      .filter(([, pct]) => pct < context.minimumValueCompletionPercent)
      .map(([id]) => id);

    issues.push({
      code: 'VALUES_INSUFFICIENT',
      severity: 'error',
      title: 'Values Not Sufficient',
      description:
        lowCompletionCriteria.length > 0
          ? `${lowCompletionCriteria.length} criteria have less than ${context.minimumValueCompletionPercent}% value completion.`
          : 'Required accepted values are not yet sufficiently complete for this tool.',
      remediation: 'Complete value entry via Values or add accepted exceptions where appropriate.',
    });
  }

  // Check POOL_NONEMPTY prerequisite (explicit: pool must have at least one item)
  if (hasPrerequisite(toolDef, 'POOL_NONEMPTY') && context.items.length === 0) {
    issues.push({
      code: 'POOL_EMPTY',
      severity: 'error',
      title: 'No Items in Pool',
      description: `${toolDef.name} requires at least one item in the pool.`,
      remediation: 'Add items to the pool before running this tool.',
    });
  }

  // Check that all required item fields exist as named session criteria
  if (
    !toolDef.toolDefinesCriteria
    && toolDef.requiredItemFields
    && toolDef.requiredItemFields.length > 0
  ) {
    const criteriaKeys = new Set(context.criteria.map(c => c.key));
    const missingFields = toolDef.requiredItemFields.filter(f => !criteriaKeys.has(f));
    if (missingFields.length > 0) {
      const fieldList = missingFields.map(f => `"${f}"`).join(', ');
      issues.push({
        code: 'REQUIRED_ITEM_FIELDS_MISSING',
        severity: 'error',
        title: 'Required Attributes Not Set Up',
        description: `${toolDef.name} requires ${missingFields.length === 1 ? 'this attribute' : 'these attributes'} as session criteria: ${fieldList}.`,
        remediation: `Add ${fieldList} as criteria in the session's Criteria tab before running this tool.`,
      });
    }
  }
  
  // Separate blockers and warnings
  const blockers = issues.filter(i => i.severity === 'error');
  const warnings = issues.filter(i => i.severity === 'warning');
  
  // Determine status
  let status: ToolCompatibilityStatus;
  if (blockers.length > 0) {
    status = 'incompatible';
  } else if (warnings.length > 0) {
    status = 'compatible_with_warnings';
  } else {
    status = 'compatible';
  }
  
  // Generate summary
  let summary: string;
  if (status === 'compatible') {
    summary = `${toolDef.name} is fully compatible with the current session.`;
  } else if (status === 'compatible_with_warnings') {
    summary = `${toolDef.name} can be used, but ${warnings.length} warning(s) should be reviewed.`;
  } else {
    summary = `${toolDef.name} cannot be used. ${blockers.length} issue(s) must be resolved.`;
  }
  
  return {
    toolId: canonicalToolId,
    status,
    issues,
    blockers,
    warnings,
    canUse: blockers.length === 0,
    summary,
  };
}

/**
 * Quick check if a tool can be used without full evaluation
 */
export function canUseTool(
  toolId: string,
  itemCount: number,
  criteriaCount: number,
  poolLocked: boolean
): boolean {
  const toolDef = resolveKnownToolDefinition(toolId);
  if (!toolDef) {
    return true; // Allow unknown tools
  }
  
  if (itemCount < toolDef.minItems) {
    return false;
  }
  
  if (
    !toolDef.toolDefinesCriteria
    && (toolDef.requiresCriteria || hasPrerequisite(toolDef, 'CRITERIA_LOCKED'))
    && criteriaCount === 0
  ) {
    return false;
  }
  
  if (hasPrerequisite(toolDef, 'POOL_LOCKED') && !poolLocked) {
    return false;
  }
  
  return true;
}

/**
 * Get list of compatible tools for a session context
 */
export function getCompatibleTools(
  context: Omit<ToolCompatibilityContext, 'toolId' | 'toolMeta'>
): Array<{ toolId: string; name: string; status: ToolCompatibilityStatus }> {
  return CANONICAL_KNOWN_TOOL_IDS.map(toolId => {
    const result = evaluateToolCompatibility({ ...context, toolId });
    const toolDef = resolveKnownToolDefinition(toolId);
    return {
      toolId,
      name: toolDef?.name ?? toolId,
      status: result.status,
    };
  });
}

/**
 * Get tool families available for selection
 */
export function getToolFamilies(): string[] {
  const families = new Set(Object.values(KNOWN_TOOLS).map(t => t.family));
  return Array.from(families);
}

/**
 * Get tools by family
 */
export function getToolsByFamily(family: string): ToolDefinition[] {
  return Object.values(KNOWN_TOOLS).filter(t => t.family === family);
}

// ============================================================================
// Formatting Helpers
// ============================================================================

/**
 * Format compatibility status for display
 */
export function formatCompatibilityStatus(status: ToolCompatibilityStatus): string {
  const labels: Record<ToolCompatibilityStatus, string> = {
    'compatible': 'Compatible',
    'compatible_with_warnings': 'Compatible (with warnings)',
    'incompatible': 'Incompatible',
  };
  return labels[status];
}

/**
 * Get CSS class for compatibility status
 */
export function getCompatibilityStatusClass(status: ToolCompatibilityStatus): string {
  const classes: Record<ToolCompatibilityStatus, string> = {
    'compatible': 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]',
    'compatible_with_warnings': 'text-[var(--mustard)] bg-[var(--mustard-dim)]',
    'incompatible': 'text-[var(--coral)] bg-[var(--coral-dim)]',
  };
  return classes[status];
}
